import { fixedUi } from '@/i18n/fixedUi';
import React, { startTransition, useCallback, useRef, useState } from 'react';
import SignedImage from '@/components/ipe/SignedImage';
import { tiktokPlatformAPI } from '@/utils/api';
import { adminAPI } from '@/utils/adminApi';
import { showToast } from '@/utils/toast';
import { chartText } from '@/i18n/chartText';
import { useTranslation } from 'react-i18next';
import { withRequiredMark } from '@/tiktok-platform/components/RequiredMark';
import TiktokUploadSourceMenu from '@/tiktok-platform/components/TiktokUploadSourceMenu';
import LibraryModalContent from '@/pages/ipe/workbench/modals/LibraryModalContent';
import { convertImageSpaceToPublishSlot, imageSpaceRecordToUploadFile, resolveImageSpacePickUrl } from '@/tiktok-platform/utils/tiktokImageSpacePick';
import { appendUniqueImageSpaceRows } from '@/pages/ipe/workbench/utils/imageSpacePageFetch';
import '@/styles/ipe/workbench/IpeModalUnified.css';
import {
  TIKTOK_CROP_SOURCE_MIN,
  TIKTOK_MAIN_DETAIL_MIN_W,
  TIKTOK_MAIN_HEIGHT,
  TIKTOK_MAIN_WIDTH,
  TIKTOK_MATERIAL_IMAGE_HINT,
  TIKTOK_MATERIAL_SIDE,
  TIKTOK_PRODUCT_IMAGE_ACCEPT,
  TIKTOK_PRODUCT_IMAGE_HINT,
  TIKTOK_PRODUCT_IMAGE_MAX_BYTES,
  TIKTOK_SQUARE_MIN,
  prepareTiktokMaterialImage,
  prepareTiktokProductImage,
  validateTiktokProductImageFormat,
} from '@/tiktok-platform/utils/tiktokProductImage';
import { useFixedAspectCropGate } from '@/components/image/useFixedAspectCropGate';
import {
  PRODUCT_CAROUSEL_ASPECT,
  PRODUCT_CAROUSEL_ASPECT_LABEL,
  PRODUCT_MATERIAL_ASPECT,
  PRODUCT_MATERIAL_ASPECT_LABEL,
} from '@/components/image/fixedAspectCrop';
import ImageSpacePreviewModal from '@/pages/ipe/workbench/components/ImageSpacePreviewModal';
import { isLikelyBrowserReachableThumb, pickBrowserPreviewImageUrl } from '@/tiktok-platform/modules/products/components/TiktokProductThumb';
import { isSignedViewPath } from '@/utils/imageDisplayAccess';
import {
  resolveUploadThumbSrc,
  shouldUseSignedThumb,
  toCanonicalUploadsPath,
} from '@/tiktok-platform/modules/products/components/publishImageUploadThumbSrc';

function PublishUploadThumb({ item }) {
  const src = resolveUploadThumbSrc(item);
  const [broken, setBroken] = useState(false);
  // 裂图时不要用文件名当 alt，否则灰框里铺满哈希名
  const alt = '';
  if (!src || broken) {
    return <span className="tko-img-upload__thumb-empty" aria-hidden="true" />;
  }
  if (!shouldUseSignedThumb(src)) {
    return (
      <img
        src={src}
        alt={alt}
        loading="eager"
        decoding="async"
        referrerPolicy="no-referrer"
        draggable={false}
        onError={() => setBroken(true)}
      />
    );
  }
  return (
    <SignedImage
      url={src}
      alt={alt}
      loading="eager"
      className="tko-img-upload__signed"
      fallback={<span className="tko-img-upload__thumb-empty" aria-hidden="true" />}
    />
  );
}

function buildUploadedSlot(file, res, { requireTiktokUrl = true, skipBlobPreview = false } = {}) {
  const tiktokUrl = String(res?.tiktokUrl || res?.url || '').trim();
  const uri = String(res?.uri || res?.tiktokUri || '').trim();
  if (requireTiktokUrl && !tiktokUrl && !uri) {
    throw new Error(res?.message || res?.msg || chartText('publishImg.noTiktokUrl', '图片上传未返回 TikTok 地址，请重试'));
  }
  // Create Product 必须有 material uri；仅有 CDN url 会在 listing_check 报 12052722
  if (requireTiktokUrl && !uri) {
    throw new Error(chartText('publishImg.noUri', '图片上传未返回平台 uri，请重试（勿用未转图的外链）'));
  }
  const rawIpe = String(res?.ipeUrl || res?.originalUrl || res?.storedUrl || '').trim();
  const rawPublic = String(
    res?.publicUrl
    || res?.previewUrlDisplay
    || res?.originalUrlDisplay
    || res?.viewUrl
    || res?.displayUrl
    || '',
  ).trim();
  // 持久化只存 canonical /uploads/…，禁止把跨端口绝对 uploads 写进表单（刷新后必裂）
  const durable = toCanonicalUploadsPath(rawIpe)
    || toCanonicalUploadsPath(rawPublic)
    || rawIpe
    || rawPublic
    || '';
  // 店铺发品：同页 blob 立刻出图。官方模板必须走 OSS 签名地址，换电脑才能看见，禁止只停在浏览器 blob。
  let previewUrl = '';
  if (!skipBlobPreview) {
    try {
      if (typeof URL !== 'undefined' && file) {
        previewUrl = URL.createObjectURL(file);
      }
    } catch {
      /* ignore */
    }
  }
  if (!previewUrl) {
    // 勿把 private-tag tiktokUrl 当预览：浏览器打不开，会显示裂图图标
    const fallback = pickBrowserPreviewImageUrl(rawPublic, durable, rawIpe);
    previewUrl = (fallback && isLikelyBrowserReachableThumb(fallback)) ? fallback : durable;
  }
  return {
    id: `${Date.now()}-${Math.random().toString(36).slice(2, 7)}`,
    name: file.name,
    previewUrl,
    publicUrl: isSignedViewPath(rawPublic) ? rawPublic : durable,
    ipeUrl: durable,
    tiktokUrl: tiktokUrl || uri,
    uri: uri || undefined,
  };
}

async function uploadLocalWithoutTiktokConvert(file, shopId = '') {
  const res = shopId
    ? await tiktokPlatformAPI.storePublishImage(shopId, file)
    : await adminAPI.storeOfficialTiktokProductTemplateImage(file);
  const slot = buildUploadedSlot(file, res, { requireTiktokUrl: false, skipBlobPreview: true });
  if (!slot.ipeUrl && !slot.publicUrl && !slot.previewUrl) {
    throw new Error(res?.message || res?.msg || chartText('publishImg.noUrl', '图片上传未返回地址，请重试'));
  }
  return slot;
}

async function uploadOne(file, shopId, imageType = 1, {
  skipTiktokConvert = false,
  fromImageSpace = false,
  imageBizType = 0,
  useCase = '',
} = {}) {
  if (skipTiktokConvert) {
    return uploadLocalWithoutTiktokConvert(file, shopId);
  }
  if (!shopId) {
    throw new Error(chartText('publishImg.pickShopFirst', '请先选择店铺后再上传图片'));
  }
  // 本地：写入图片空间 + Tiktok 转图；图片空间选用：只转 Tiktok
  // imageBizType：0=货品图，1=外包装图（官方：入参1返回的url用于货品外包装）
  const res = await tiktokPlatformAPI.uploadProductPic(shopId, file, {
    imageType,
    imageBizType,
    useCase,
    fromImageSpace: Boolean(fromImageSpace),
  });
  return buildUploadedSlot(file, res, { requireTiktokUrl: true });
}

/** 发品图片上传：单图占满槽位；多图可配置上传格固定在最前、新图插到最前；多图可拖拽调序。默认按 TikTok 商品图规则校验并压缩。 */
export default function PublishImageUploadZone({
  label,
  hint,
  value = [],
  multiple = false,
  maxCount = 9,
  shopId,
  onChange,
  compact = false,
  /** 标题放在上传框内部（对齐商家后台商品图槽位） */
  labelInside = false,
  round = false,
  /** 是否启用 TikTok 商品图尺寸/格式/10MB 规则（默认开启） */
  enforceTiktokRules = true,
  /**
   * 官方产品模板无店铺：跳过 TikTok URL 转图，只落 IPE 原图（仍走 3:4 比例校验/裁剪）。
   */
  skipTiktokConvert = false,
  /**
   * TikTok image_type：1主图 2细节图 7详情图。
   * 色块图由发品流程按主图自动裁切上传（对齐 Shein，用户无需手传）；可通过 prepareFile 自定义预处理。
   */
  imageType = 1,
  /**
   * Tiktok imageBizType：0=货品图（主图/细节/素材），1=外包装图。
   * 外包装槽位必须传 1，否则会得到 product/open 链，发品时报 Invalid image links。
   */
  imageBizType = 0,
  /**
   * TTS Upload Image use_case：MAIN_IMAGE（默认）/ ATTRIBUTE_IMAGE（SKU 变体图）/
   * SIZE_CHART_IMAGE（尺码表图）/ CERTIFICATION_IMAGE（认证图）。
   */
  useCase = '',
  /**
   * 校验/裁剪规则：product=服饰主图细节 3:4；material=素材图 1:1≥800。
   */
  imageRule = 'product',
  /** 多图：上传格固定在最前面 */
  uploadSlotFirst = false,
  /** 多图：新上传图片插到列表最前（紧贴上传格） */
  prependNew = false,
  /** 多图：支持拖拽调整缩略图顺序 */
  reorderable = false,
  /** 是否允许从图片空间选图（默认开启） */
  enableImageSpacePick = true,
  /** 上传前自定义处理（如色块图裁 80×80）；在 TikTok 规则校验之前执行 */
  prepareFile = null,
}) {
  useTranslation('workbench');
  const inputRef = useRef(null);
  const uploadAnchorRef = useRef(null);
  const dragIndexRef = useRef(-1);
  const imageSpaceRowsRef = useRef([]);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [previewItem, setPreviewItem] = useState(null);
  const [draggingIndex, setDraggingIndex] = useState(-1);
  const [dropTargetIndex, setDropTargetIndex] = useState(-1);
  const [uploadMenuOpen, setUploadMenuOpen] = useState(false);
  const [libraryOpen, setLibraryOpen] = useState(false);
  const [librarySelection, setLibrarySelection] = useState([]);
  const list = Array.isArray(value) ? value : (value ? [value] : []);
  const isMaterialRule = imageRule === 'material';
  const defaultHint = isMaterialRule ? TIKTOK_MATERIAL_IMAGE_HINT : TIKTOK_PRODUCT_IMAGE_HINT;
  const displayHint = hint || (enforceTiktokRules && !compact && !labelInside ? chartText(isMaterialRule ? 'publishImg.hintMaterialTt' : 'publishImg.hintProductTt', defaultHint) : '');
  const canUploadMore = multiple ? list.length < maxCount : list.length === 0;
  const canReorder = Boolean(multiple && reorderable && list.length > 1);
  const remainSlots = multiple ? Math.max(0, maxCount - list.length) : (list.length === 0 ? 1 : 0);
  const { ensureAspectFile, cropModal, cropping } = useFixedAspectCropGate(
    isMaterialRule
      ? {
        aspect: PRODUCT_MATERIAL_ASPECT,
        aspectLabel: PRODUCT_MATERIAL_ASPECT_LABEL,
        title: chartText('publishImg.cropSquareTitle', '裁剪为 1:1 素材图'),
        hint: chartText('publishImg.cropSquareHintTt', '素材/SKU 变体图须为 1:1，边长≥300px（推荐 800×800，平台允许 300～4000，≤10MB）。比例或尺寸不合规时请拖动裁剪框选择要保留的区域。'),
        outputWidth: TIKTOK_MATERIAL_SIDE,
        outputHeight: TIKTOK_MATERIAL_SIDE,
        maxBytes: TIKTOK_PRODUCT_IMAGE_MAX_BYTES,
        minCropEdge: TIKTOK_SQUARE_MIN,
        minOutputWidth: TIKTOK_SQUARE_MIN,
        minOutputHeight: TIKTOK_SQUARE_MIN,
      }
      : {
        title: chartText('publishImg.cropCarouselTitle', '裁剪为 3:4 商品图'),
        hint: chartText('publishImg.cropCarouselHintTt', '主图/细节图按平台要求裁为 3:4，边长 300～4000px（推荐 1200×1600，≤10MB）。比例或尺寸不合规时请拖动裁剪框选择要保留的区域。'),
        aspect: PRODUCT_CAROUSEL_ASPECT,
        aspectLabel: PRODUCT_CAROUSEL_ASPECT_LABEL,
        outputWidth: TIKTOK_MAIN_WIDTH,
        outputHeight: TIKTOK_MAIN_HEIGHT,
        maxBytes: TIKTOK_PRODUCT_IMAGE_MAX_BYTES,
        minCropEdge: TIKTOK_CROP_SOURCE_MIN,
        minOutputWidth: TIKTOK_MAIN_DETAIL_MIN_W,
        minOutputHeight: Math.ceil(TIKTOK_MAIN_DETAIL_MIN_W / PRODUCT_CAROUSEL_ASPECT),
      },
  );

  const prepareUploadFile = useCallback(async (raw) => {
    let file = raw;
    if (typeof prepareFile === 'function') {
      file = await prepareFile(raw);
    }
    if (!enforceTiktokRules) return file;
    const formatError = validateTiktokProductImageFormat(file);
    if (formatError) throw new Error(formatError);
    const aspectOk = await ensureAspectFile(file);
    if (isMaterialRule) {
      return prepareTiktokMaterialImage(aspectOk, { requireAspect: true });
    }
    return prepareTiktokProductImage(aspectOk, { requireAspect: true });
  }, [enforceTiktokRules, ensureAspectFile, prepareFile, isMaterialRule]);

  const rememberImageSpaceRows = useCallback((rows) => {
    if (!Array.isArray(rows) || rows.length === 0) return;
    imageSpaceRowsRef.current = appendUniqueImageSpaceRows(imageSpaceRowsRef.current, rows);
  }, []);

  const applyFiles = async (fileList) => {
    const files = Array.from(fileList || []).filter(Boolean);
    if (files.length === 0) return;

    const remain = multiple ? Math.max(0, maxCount - list.length) : 1;
    const picked = files.slice(0, remain);
    if (picked.length === 0) {
      showToast(chartText('publishImg.maxCount', `最多上传 ${maxCount} 张`, { count: maxCount }), 'warning');
      return;
    }

    setUploading(true);
    try {
      const uploaded = [];
      for (const raw of picked) {
        const file = await prepareUploadFile(raw);
        const main = await uploadOne(file, shopId, imageType, {
          skipTiktokConvert,
          imageBizType,
          useCase,
        });
        uploaded.push(main);
      }
      if (multiple) {
        const next = prependNew
          ? [...uploaded, ...list]
          : [...list, ...uploaded];
        onChange?.(next.slice(0, maxCount));
      } else {
        onChange?.(uploaded[0] || null);
      }
      showToast(uploaded.length > 1 ? chartText('publishImg.uploadedMany', `已上传 ${uploaded.length} 张`, { count: uploaded.length }) : chartText('publishImg.uploaded', '图片已上传'), 'success');
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg.includes('已取消裁剪')) {
        showToast(chartText('publishImg.cropCancelled', '已取消裁剪，未上传该图片'), 'info');
      } else {
        showToast(msg || chartText('publishImg.uploadFail', '图片上传失败'), 'error', Math.min(12000, Math.max(5000, msg.length * 60)));
      }
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  const applyImageSpaceRecords = async (records) => {
    const picked = (Array.isArray(records) ? records : []).filter(Boolean);
    if (picked.length === 0) return;
    const remain = multiple ? Math.max(0, maxCount - list.length) : 1;
    const batch = picked.slice(0, remain);
    if (batch.length === 0) {
      showToast(chartText('publishImg.maxCount', `最多上传 ${maxCount} 张`, { count: maxCount }), 'warning');
      return;
    }
    if (batch.length < picked.length) {
      showToast(chartText('publishImg.overLimit', `超出上限，仅处理前 ${batch.length} 张`, { count: batch.length }), 'warning');
    }

    setUploading(true);
    try {
      const uploaded = [];
      for (const image of batch) {
        // 印花图等：直接用空间原图，不落库、不转 Tiktok
        if (skipTiktokConvert) {
          uploaded.push(await convertImageSpaceToPublishSlot({
            shopId,
            image,
            imageType,
            useCase,
            skipTiktokConvert: true,
          }));
          continue;
        }
        if (enforceTiktokRules) {
          const rawFile = await imageSpaceRecordToUploadFile(image);
          const file = await prepareUploadFile(rawFile);
          const slot = await uploadOne(file, shopId, imageType, {
            skipTiktokConvert: false,
            fromImageSpace: true,
            imageBizType,
            useCase,
          });
          // 保留空间原图地址作 ipeUrl，避免只剩 tiktok 外链导致站内预览不稳
          const durable = toCanonicalUploadsPath(resolveImageSpacePickUrl(image))
            || slot.ipeUrl
            || slot.publicUrl;
          uploaded.push({
            ...slot,
            ipeUrl: durable || slot.ipeUrl,
            publicUrl: slot.publicUrl || durable,
            previewUrl: slot.previewUrl || durable,
          });
        } else {
          uploaded.push(await convertImageSpaceToPublishSlot({
            shopId,
            image,
            imageType,
            imageBizType,
            useCase,
            skipTiktokConvert: false,
          }));
        }
      }
      if (multiple) {
        const next = prependNew
          ? [...uploaded, ...list]
          : [...list, ...uploaded];
        onChange?.(next.slice(0, maxCount));
      } else {
        onChange?.(uploaded[0] || null);
      }
      showToast(uploaded.length > 1 ? chartText('publishImg.pickedMany', `已选用 ${uploaded.length} 张`, { count: uploaded.length }) : chartText('publishImg.picked', '图片已选用'), 'success');
    } catch (e) {
      const msg = String(e?.message || '');
      if (msg.includes('已取消裁剪')) {
        showToast(chartText('publishImg.cropCancelPick', '已取消裁剪，未选用该图片'), 'info');
      } else {
        showToast(msg || chartText('photoLib.pickFailed', '图片选用失败'), 'error', Math.min(12000, Math.max(5000, msg.length * 60)));
      }
    } finally {
      setUploading(false);
    }
  };

  const openUploadMenu = () => {
    if (uploading || cropping) return;
    if (enableImageSpacePick) {
      setUploadMenuOpen(true);
      return;
    }
    inputRef.current?.click();
  };

  const handleLibraryClose = () => {
    setLibrarySelection([]);
    setLibraryOpen(false);
  };

  const handleLibraryToggle = useCallback((imageId) => {
    const id = String(imageId);
    startTransition(() => {
      setLibrarySelection((prev) => {
        if (prev.some((x) => String(x) === id)) {
          return prev.filter((x) => String(x) !== id);
        }
        if (!multiple) return [id];
        const limit = remainSlots > 0 ? remainSlots : maxCount;
        if (prev.length >= limit) {
          showToast(chartText('photoLib.remainMax', `最多再选 ${limit} 张`, { count: limit }), 'warning');
          return prev;
        }
        return [...prev, id];
      });
    });
  }, [multiple, remainSlots, maxCount]);

  const handleLibraryConfirm = async (positionArray) => {
    const ids = (positionArray || []).filter((x) => x != null && x !== '');
    if (ids.length === 0) {
      showToast(chartText('photoLib.pickOne', fixedUi("请至少选择一张图片")), 'info');
      return;
    }
    const limit = multiple ? (remainSlots > 0 ? remainSlots : maxCount) : 1;
    const pickedIds = ids.slice(0, limit);
    const records = pickedIds
      .map((id) => imageSpaceRowsRef.current.find((row) => String(row?.id) === String(id)))
      .filter(Boolean);
    if (records.length === 0) {
      showToast(chartText('photoLib.invalidImages', fixedUi("所选图片无效或已失效，请重试")), 'warning');
      return;
    }
    handleLibraryClose();
    await applyImageSpaceRecords(records);
  };

  const removeAt = (id) => {
    if (multiple) onChange?.(list.filter((item) => item.id !== id));
    else onChange?.(null);
  };

  const moveItem = (fromIndex, toIndex) => {
    if (!canReorder) return;
    if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return;
    if (fromIndex >= list.length || toIndex >= list.length) return;
    const next = list.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    onChange?.(next);
  };

  const clearReorderState = () => {
    dragIndexRef.current = -1;
    setDraggingIndex(-1);
    setDropTargetIndex(-1);
  };

  const uploadButton = canUploadMore ? (
    <button
      ref={uploadAnchorRef}
      type="button"
      className={`tko-img-upload__drop${dragOver ? ' tko-img-upload__drop--over' : ''}${uploading ? ' tko-img-upload__drop--busy' : ''}`}
      disabled={uploading || cropping}
      aria-busy={uploading || cropping}
      aria-haspopup={enableImageSpacePick ? 'menu' : undefined}
      onClick={openUploadMenu}
      onDragEnter={(e) => {
        if (dragIndexRef.current >= 0) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragOver={(e) => {
        if (dragIndexRef.current >= 0) return;
        e.preventDefault();
        setDragOver(true);
      }}
      onDragLeave={(e) => {
        e.preventDefault();
        setDragOver(false);
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        if (dragIndexRef.current >= 0) {
          clearReorderState();
          return;
        }
        applyFiles(e.dataTransfer?.files);
      }}
    >
      {uploading || cropping ? (
        <span className="tko-img-upload__busy" aria-live="polite">
          <span className="tko-img-upload__spinner" aria-hidden="true" />
          <span className="tko-img-upload__busy-text">{cropping ? chartText('publishImg.cropping', fixedUi("裁剪中")) : chartText('photoLib.uploading', fixedUi("上传中"))}</span>
        </span>
      ) : labelInside && label ? (
        <>
          <span className="tko-img-upload__slot-label">{withRequiredMark(label)}</span>
          <span className="tko-img-upload__slot-action">{chartText('photoLib.clickUpload', fixedUi("点击上传"))}</span>
        </>
      ) : (
        <>
          <span className="tko-img-upload__plus">+</span>
          <span>{chartText('photoLib.clickUpload', fixedUi("点击上传"))}</span>
          {!compact ? <span className="tko-img-upload__sub">{displayHint || TIKTOK_PRODUCT_IMAGE_HINT}</span> : null}
        </>
      )}
    </button>
  ) : null;

  const thumbNodes = list.map((item, index) => (
    <div
      key={item.id || `thumb-${index}`}
      className={[
        'tko-img-upload__thumb',
        canReorder ? 'tko-img-upload__thumb--reorderable' : '',
        draggingIndex === index ? 'is-dragging' : '',
        dropTargetIndex === index ? 'is-drop-target' : '',
      ].filter(Boolean).join(' ')}
      draggable={canReorder}
      title={canReorder ? chartText('publishImg.reorder', '拖拽调整顺序') : undefined}
      onDragStart={(e) => {
        if (!canReorder) return;
        if (e.target.closest('.tko-img-upload__remove, .tko-img-upload__preview')) {
          e.preventDefault();
          return;
        }
        dragIndexRef.current = index;
        setDraggingIndex(index);
        e.dataTransfer.effectAllowed = 'move';
        e.dataTransfer.setData('text/plain', String(index));
        try {
          e.dataTransfer.setData('application/x-ipe-tiktok-img-reorder', String(index));
        } catch {
          /* ignore */
        }
      }}
      onDragOver={(e) => {
        if (!canReorder || dragIndexRef.current < 0) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';
        if (dropTargetIndex !== index) setDropTargetIndex(index);
      }}
      onDragLeave={() => {
        if (dropTargetIndex === index) setDropTargetIndex(-1);
      }}
      onDrop={(e) => {
        if (!canReorder) return;
        e.preventDefault();
        e.stopPropagation();
        const from = dragIndexRef.current;
        clearReorderState();
        moveItem(from, index);
      }}
      onDragEnd={() => {
        clearReorderState();
      }}
    >
      <PublishUploadThumb item={item} />
      <button
        type="button"
        className="tko-img-upload__remove"
        aria-label={chartText('publishImg.remove', '移除图片')}
        onClick={() => removeAt(item.id)}
      >
        ×
      </button>
      <button
        type="button"
        className="tko-img-upload__preview"
        onClick={() => setPreviewItem(item)}
      >
        {fixedUi("预览")}
      </button>
    </div>
  ));

  return (
    <div className={`tko-img-upload${compact ? ' tko-img-upload--compact' : ''}${labelInside ? ' tko-img-upload--label-inside' : ''}${round ? ' tko-img-upload--round' : ''}`}>
      {(label || displayHint) && !labelInside ? (
        <div className="tko-img-upload__head">
          {label ? <span className="tko-field__label">{withRequiredMark(label)}</span> : null}
          {displayHint ? <span className="tko-img-upload__hint">{displayHint}</span> : null}
        </div>
      ) : null}

      <div className="tko-img-upload__grid">
        {uploadSlotFirst ? (
          <>
            {uploadButton}
            {thumbNodes}
          </>
        ) : (
          <>
            {thumbNodes}
            {uploadButton}
          </>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept={(enforceTiktokRules || skipTiktokConvert) ? TIKTOK_PRODUCT_IMAGE_ACCEPT : 'image/jpeg,image/jpg,image/png,image/webp,.jpg,.jpeg,.png,.webp'}
        multiple={multiple}
        hidden
        onChange={(e) => applyFiles(e.target.files)}
      />

      <ImageSpacePreviewModal
        open={Boolean(previewItem)}
        initialImage={previewItem ? {
          id: previewItem.id,
          name: previewItem.name || chartText('publishSpec.preview', fixedUi("预览")),
          url: resolveUploadThumbSrc(previewItem),
          src: resolveUploadThumbSrc(previewItem),
          previewUrl: previewItem.previewUrl || '',
          publicUrl: previewItem.publicUrl || '',
          viewUrl: previewItem.publicUrl || '',
          displayUrl: previewItem.publicUrl || '',
          ipeUrl: previewItem.ipeUrl || '',
          source: 'upload',
        } : null}
        navList={list.map((item) => ({
          id: item.id,
          name: item.name || chartText('publishSpec.preview', fixedUi("预览")),
          url: resolveUploadThumbSrc(item),
          src: resolveUploadThumbSrc(item),
          previewUrl: item.previewUrl || '',
          publicUrl: item.publicUrl || '',
          viewUrl: item.publicUrl || '',
          displayUrl: item.publicUrl || '',
          ipeUrl: item.ipeUrl || '',
          source: 'upload',
        }))}
        onClose={() => setPreviewItem(null)}
        resolveUrl={(_id, img) => resolveUploadThumbSrc(img) || String(img?.url || img?.src || '').trim()}
      />

      {enableImageSpacePick ? (
        <>
          <TiktokUploadSourceMenu
            open={uploadMenuOpen}
            anchorRef={uploadAnchorRef}
            onClose={() => setUploadMenuOpen(false)}
            disabled={uploading || cropping}
            onPickLocal={() => inputRef.current?.click()}
            onPickImageSpace={() => setLibraryOpen(true)}
          />
          <LibraryModalContent
            isOpen={libraryOpen}
            onClose={handleLibraryClose}
            images={[]}
            selection={librarySelection}
            onToggle={handleLibraryToggle}
            maxSelection={multiple ? (remainSlots > 0 ? remainSlots : maxCount) : 1}
            onConfirm={handleLibraryConfirm}
            onImagesAppended={rememberImageSpaceRows}
            uploadOriginSource="upload"
            showToast={showToast}
            deferUsageBump
            selectAllLabel={chartText('photoLib.selectLoaded', '全选已加载')}
            closeOnBackdropClick={false}
          />
        </>
      ) : null}
      {cropModal}
    </div>
  );
}
